import { ReactElement, ReactNode } from "react";
import { Link, navigate } from "gatsby";
import clsx from "clsx";

import Icon from "@/components/elements/Icon";
import {
  fathomTrack,
  isOutboundLink,
  fathomEventsAvailable,
  type FathomAnalyticsProps,
} from "@/base/utils/fathom-analytics";
import {
  GoogleTagManagerEvents,
  gtmEvent,
  type GoogleTagManagerProps,
} from "@/base/utils/google-tag-manager";
import type { DataAnalyticsProps } from "@/base/types/data-analytics";
import * as styles from "./styles.module.scss";

interface Props
  extends Pick<LinkElement, "title" | "type" | "url" | "with_icon"> {
  className?: string;
  children?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
  variant?: "default" | "button_primary" | "button_outlined";
  dataAnalytics?: DataAnalyticsProps;
}

const handleClickOnReferences = (url: string) => {
  const id = url.split("#")[1];
  const yOffset = -120;
  const element = document.getElementById(id);
  const y =
    element &&
    element.getBoundingClientRect().top + window.pageYOffset + yOffset;

  return window?.scrollTo({ top: y || 0, behavior: "smooth" });
};

export function LinkComponent({
  title,
  type,
  url,
  className = "",
  children,
  with_icon,
  tooltip,
  disabled,
  variant = "default",
  dataAnalytics,
}: Props): ReactElement {
  const isBrowser = typeof window !== "undefined";
  const asButton = variant.includes("button");
  const linkClass = clsx(
    styles.link,
    { [styles.withIcon]: with_icon },
    className
  );

  const buttonClass = clsx(
    styles.button,
    {
      [styles.primary]: variant === "button_primary",
      [styles.outlined]: variant === "button_outlined",
      [styles.withIcon]: !!with_icon,
    },
    className
  );

  if (type === "internal") {
    return (
      <Link
        onClick={(e) => {
          if (dataAnalytics) {
            e.preventDefault();
            if (dataAnalytics.fathom) fathomTrack(dataAnalytics.fathom);
            if (dataAnalytics.gtm) gtmEvent(dataAnalytics.gtm);
            navigate(url);
          }

          if (disabled) {
            e.preventDefault();
          }
        }}
        to={url}
        className={clsx(asButton ? buttonClass : linkClass, {
          [styles.disabled]: disabled,
          disabled,
        })}
        title={tooltip}
      >
        {title || children}
        {with_icon && <Icon name="arrowUpRight" width={24} height={24} />}
      </Link>
    );
  }

  if (url && url.includes?.("#") && url.split("")[0] === "#") {
    return (
      <span
        tabIndex={0}
        role="link"
        className={asButton ? buttonClass : linkClass}
        onClick={() => handleClickOnReferences(url)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleClickOnReferences(url);
          }
        }}
        aria-label={title || `Anchor link - ${children}`}
      >
        {title || children}
      </span>
    );
  }

  const fathomOutboundLinkEvent: FathomAnalyticsProps = {
    eventName: fathomEventsAvailable["outbound-link-click"],
    props: {
      url,
      location: isBrowser ? window.location.pathname : "",
    },
  };

  const googleOutboundLinkEvent: GoogleTagManagerProps = {
    event: GoogleTagManagerEvents.OutboundLinkClick,
    props: {
      url,
    },
  };

  return (
    <a
      title={tooltip}
      onClick={(e) => {
        if (dataAnalytics) {
          e.preventDefault();
          if (dataAnalytics.fathom) fathomTrack(dataAnalytics.fathom);
          if (dataAnalytics.gtm) gtmEvent(dataAnalytics.gtm);
          if (isBrowser) {
            window.open(url, "_blank");
          }
        }

        // Track Outbound Links
        if (isOutboundLink(url)) {
          e.preventDefault();
          fathomTrack(fathomOutboundLinkEvent);
          gtmEvent(googleOutboundLinkEvent);
          if (isBrowser) {
            window.open(url, "_blank");
          }
        }
      }}
      href={url}
      className={asButton ? buttonClass : linkClass}
      rel="noreferrer noopener"
      target="_blank"
    >
      {title || children}
      {with_icon && <Icon height={24} width={24} name="arrowUpRight" />}
    </a>
  );
}
